/**
 * Auth Context for Vibe Designer — AuthProvider component only.
 *
 * E.14 (2026-04-29): the AuthContext + AuthState/AuthContextValue types +
 * useAuth hook moved to ./useAuth so this file exports only React
 * components (Vite Fast Refresh requirement).
 *
 * Token is stored in localStorage as 'vibe_designer_token'.
 * User info stored as 'vibe_designer_user'.
 *
 * §9.1 (2026-09-05): the preview door (/api/preview/<id>/…) is cookie-gated.
 * The cookie is minted BEFORE this provider reports `isLoading: false` on an
 * authenticated boot — at every one of the THREE sites that do so (the OAuth
 * callback, the plain valid-token branch, the silent boot-refresh branch) —
 * through one `settleAuthenticated()` helper, so the first <img>/iframe paints
 * with the cookie present instead of racing the mint. A failed mint is logged
 * and is NOT fatal: the user is still authenticated, previews 401 until the
 * next re-mint (each proactive token refresh, and every PREVIEW_COOKIE_REMINT_MS).
 */

import { useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/authService';
import { ensurePreviewCookie, PREVIEW_COOKIE_REMINT_MS } from '../services/previewAuth';
import { AuthContext, AuthState } from './useAuth';

async function withPreviewCookie(): Promise<void> {
  try {
    await ensurePreviewCookie();
  } catch (e) {
    console.warn('[Auth] preview cookie mint failed — previews will 401 until the next re-mint', e);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // The ONE place an authenticated boot is reported. Mint first, then flip.
  const settleAuthenticated = async () => {
    await withPreviewCookie();
    setState({
      user: authService.getUser(),
      token: authService.getToken(),
      isLoading: false,
      isAuthenticated: true,
    });
  };

  // On mount: check for existing token or handle callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code && window.location.pathname === '/auth/callback') {
      // Handle OAuth callback — exchange code for tokens
      authService.handleCallback(code).then((success) => {
        // Clean URL
        window.history.replaceState({}, '', '/');
        if (success) {
          void settleAuthenticated();
        } else {
          console.error('[Auth] Token exchange failed');
          setState(prev => ({ ...prev, isLoading: false }));
        }
      });
    } else {
      // Existing token, VALIDITY-gated (2026-06-25). A present-but-expired token
      // must NOT mount the app (it fired a 401 storm + the "unauthorized" voice
      // banner pre-fix). Three outcomes:
      //   - valid                          → authenticated;
      //   - expired BUT a refresh token    → SILENTLY REFRESH before deciding
      //     exists                           (keep isLoading=true so
      //                                       ProtectedRoute shows the spinner,
      //                                       never a /login flash);
      //   - expired + no refresh, or none  → logged out (→ /login).
      const token = authService.getToken();
      if (token && authService.isAuthenticated()) {
        void settleAuthenticated();
      } else if (token && authService.getRefreshToken()) {
        authService.refreshAccessToken().then((ok) => {
          if (ok) {
            void settleAuthenticated();
          } else {
            setState({ user: null, token: null, isLoading: false, isAuthenticated: false });
          }
        });
      } else {
        if (token) authService.clearStoredAuth();
        setState({ user: null, token: null, isLoading: false, isAuthenticated: false });
      }
    }
    // settleAuthenticated closes over setState only; this is the boot effect
    // and runs once (the lint rule does not flag it, so no directive here).
  }, []);

  // Proactive silent refresh (2026-06-25): keep the access token fresh while the
  // session is alive so it never hard-expires at 8h. Reschedules from each
  // renewed token's exp; also refreshes when the tab returns to focus/visibility
  // (covers laptop sleep, where the background timer is throttled). On refresh
  // failure the session drops to unauthenticated → ProtectedRoute /login.
  // §9.1: every successful refresh re-mints the preview cookie, and a timer
  // re-mints inside the cookie's TTL regardless.
  useEffect(() => {
    if (!state.isAuthenticated || !state.token) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const runRefresh = async () => {
      const ok = await authService.refreshAccessToken();
      if (cancelled) return;
      if (ok) {
        void withPreviewCookie();
        // New token → bump state so this effect re-runs and reschedules.
        setState((prev) => ({ ...prev, token: authService.getToken() }));
      } else {
        setState((prev) => ({ ...prev, user: null, token: null, isAuthenticated: false }));
      }
    };

    const delay = authService.nextRefreshDelayMs(state.token);
    if (delay !== null) {
      timer = setTimeout(() => { void runRefresh(); }, delay);
    }

    const remint = setInterval(() => { void withPreviewCookie(); }, PREVIEW_COOKIE_REMINT_MS);

    const onForeground = () => {
      // Refresh on return-to-foreground when the token is within the lead window
      // or already past — isTokenExpired(token, 120) is true in that range.
      if (
        document.visibilityState === 'visible' &&
        state.token &&
        authService.isTokenExpired(state.token, 120)
      ) {
        void runRefresh();
      }
    };
    document.addEventListener('visibilitychange', onForeground);
    window.addEventListener('focus', onForeground);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      clearInterval(remint);
      document.removeEventListener('visibilitychange', onForeground);
      window.removeEventListener('focus', onForeground);
    };
  }, [state.token, state.isAuthenticated]);

  const login = (redirectPath?: string) => {
    // Store the intended destination so we can redirect after login
    if (redirectPath) {
      sessionStorage.setItem('auth_redirect', redirectPath);
    }
    authService.login();
  };

  const logout = () => {
    authService.logout();
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
